import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, Receipt, Camera } from "lucide-react";
import { useSession } from "@/hooks/use-session";
import { supabase } from "@/lib/supabase";
import { formatMoney } from "@/lib/currency";
import { SkeletonLoader, EmptyState } from "@/components/system";

type ReceiptRow = {
  id: string;
  store_name: string | null;
  store_chain: string | null;
  purchased_at: string;
  total_amount: number | null;
  currency: string | null;
  status: string;
  extraction_confidence: string | null;
};

export function FinanceReceipts() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);

  const receipts = useQuery({
    queryKey: ["finance_receipts", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      const { data, error } = await (supabase as never as { from: (t: string) => any })
        .from("finance_receipt")
        .select("id, store_name, store_chain, purchased_at, total_amount, currency, status, extraction_confidence")
        .eq("user_id", userId!)
        .order("purchased_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as ReceiptRow[];
    },
  });

  async function handleUpload(file: File) {
    setUploading(true);
    try {
      const dataUrl = await readAsDataURL(file);
      const res = await fetch("/api/finance/receipts/extract", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({ image: dataUrl, image_type: file.type }),
      });
      if (!res.ok) {
        alert("Receipt extraction failed: " + (await res.text()));
        return;
      }
      const json = (await res.json()) as { receipt_id?: string };
      if (json.receipt_id) {
        navigate(`/finance/receipts/${json.receipt_id}`);
      } else {
        queryClient.invalidateQueries({ queryKey: ["finance_receipts", userId] });
      }
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-6)" }}>
      <header style={{ display: "flex", alignItems: "baseline", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <Link to="/finance" style={{ color: "var(--fg-3)", textDecoration: "none" }}>
          <ArrowLeft size={16} style={{ verticalAlign: "middle" }} /> Finance
        </Link>
        <h1 style={{ margin: 0, fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 28 }}>Receipts</h1>
        <div style={{ flex: 1 }} />
        <label style={{ position: "relative", display: "inline-flex" }}>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
            }}
            disabled={uploading}
          />
          <span
            style={{
              background: "var(--accent)",
              color: "var(--accent-on)",
              padding: "10px 16px",
              borderRadius: 10,
              fontSize: "var(--type-body-s)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Camera size={14} /> {uploading ? "Extracting…" : "Upload receipt"}
          </span>
        </label>
      </header>

      {receipts.isLoading ? (
        <SkeletonLoader variant="card" height={200} />
      ) : (receipts.data ?? []).length === 0 ? (
        <EmptyState
          headline="No receipts yet."
          sub="Snap a photo of a Coop / Migros / Lidl receipt — Haus extracts items + categorizes spend."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(receipts.data ?? []).map((r) => (
            <Link
              key={r.id}
              to={`/finance/receipts/${r.id}`}
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto auto",
                alignItems: "center",
                gap: 12,
                padding: "14px 16px",
                border: "1px solid var(--hair)",
                borderRadius: "var(--radius-card)",
                background: "var(--bg-2)",
                color: "var(--fg)",
                textDecoration: "none",
              }}
            >
              <Receipt size={20} strokeWidth={1.5} color="var(--accent)" />
              <div>
                <div style={{ fontWeight: 500 }}>{r.store_name ?? "(unknown)"}</div>
                <span className="mono" style={{ fontSize: 11, color: "var(--fg-3)" }}>
                  {new Date(r.purchased_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
              <span className="mono" style={{ fontSize: 16, fontWeight: 500 }}>
                {formatMoney(r.total_amount, r.currency ?? "CHF")}
              </span>
              <span
                style={{
                  fontSize: 11,
                  padding: "2px 8px",
                  borderRadius: 6,
                  background:
                    r.status === "verified"
                      ? "var(--severity-ok-wash, #ddebd9)"
                      : r.status === "rejected"
                        ? "var(--severity-bad-wash, #f6dcd6)"
                        : "var(--chip)",
                  color: "var(--fg-2)",
                }}
              >
                {r.status.replace("_", " ")}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function readAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
